<template>
  <section class="product-list-section container-fluid d-flex justify-content-center px-0">
    <div class="products-grid container mt-5 px-3">
      <div class="container-fluid px-0">
        <!-- Categories always visible; only products area shows loading state -->
        <div class="container-fluid px-0 d-flex justify-content-center">
          <div class="row px-0 mx-0 container">
            <!-- Filters Sidebar - Always visible when not in initial loading -->
            <div class="col-lg-3 col-md-4">
              <button
                  class="btn btn-dark w-100 my-3 d-lg-none"
                  type="button"
                  data-bs-toggle="modal"
                  data-bs-target="#filtersModal"
                >
                <i class="fas fa-filter me-2"></i>
                Mostrar Filtros
              </button>

              <div class="collapse d-lg-block" id="filtersCollapse">
                <div class="filters-section">
                  <!-- Type Filter -->
                  <div class="category-filters mb-4">
                    <div class="category-buttons d-flex flex-wrap gap-2">
                      <button
                        class="category-btn filter-btn"
                        :class="{ 'active': !selectedTypes.length }"
                        @click="clearAllFilters"
                      >
                        {{ isMerchPage ? 'MERCHANDISING' : 'CATÁLOGO' }}
                      </button>
                      <button
                        v-for="type in filteredTypeOptions"
                        :key="type.id"
                        class="category-btn filter-btn"
                        :class="{ 'active': selectedTypes.includes(type.id) }"
                        @click="toggleCategory(type.id)"
                      >
                        {{ type.name.toUpperCase() }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Products Grid -->
            <div class="col-lg-9 col-md-8">
              <div v-if="loadError" class="alert alert-danger" role="alert">
                {{ loadError }}
                <button class="btn btn-outline-danger btn-sm" @click="loadProducts(true)">Reintentar</button>
              </div>
              <!-- Products loading skeleton (initial load, filters, pagination) -->
              <div v-if="skeletonActive" class="skeleton-layer">
                <div class="row g-4">
                  <div class="col-xl-4 col-lg-6 col-12" v-for="i in 6" :key="i">
                    <div class="skeleton-card">
                      <div class="skeleton-image"></div>
                      <div class="skeleton-content">
                        <div class="skeleton-text-sm"></div>
                        <div class="skeleton-text-lg"></div>
                        <div class="skeleton-text-md"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Products loaded -->
              <div
                v-else-if="filteredProducts.length > 0"
                class="products-grid-content"
                :class="{ 'is-visible': productsRevealed }"
              >
                <div class="row g-4">
                  <div
                    class="col-xl-4 col-lg-6 col-12 product-column"
                    v-for="product in filteredProducts"
                    :key="product.id"
                  >
                    <ProductCard
                      class="catalog-product-card"
                      :product="product"
                      :showName="true"
                      :showFirstImage="false"
                      :minHeight="'350px'"
                      :fadeImage="false"
                    />
                  </div>
                </div>

                <!-- Pagination Controls -->
                <div class="pagination-container text-center mt-5" v-if="totalPages > 1">
                  <div class="pagination-controls">
                    <button 
                      class="pagination-btn"
                      :class="{ 'disabled': currentPage === 1 }"
                      @click="goToPage(currentPage - 1)"
                      :disabled="currentPage === 1 || isLoadingMore"
                    >
                      <i class="fas fa-chevron-left"></i>
                    </button>
                    
                    <template v-for="page in paginationItems" :key="page">
                      <span v-if="page === '...'" class="pagination-ellipsis">...</span>
                      <button
                        v-else
                        class="pagination-btn fs-2 p-4"
                        :class="{ 'active': page === currentPage }"
                        @click="goToPage(page)"
                        :disabled="isLoadingMore"
                      >
                        {{ page }}
                      </button>
                    </template>
                    
                    <button 
                      class="pagination-btn"
                      :class="{ 'disabled': currentPage === totalPages }"
                      @click="goToPage(currentPage + 1)"
                      :disabled="currentPage === totalPages || isLoadingMore"
                    >
                      <i class="fas fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
              </div>
              
              <!-- no products -->
              <div class="text-center h-75 d-flex align-items-center justify-content-center mt-5" v-else>
                <div>
                  <img src="@/assets/icons/cannabis.svg" alt="cannabis plant icon" class="img-fluid mb-4 col-5" />
                  <h3 class="text-white fs-5">No se encontraron productos.</h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Filters Modal for Mobile -->
  <div class="modal fade mt-5" id="filtersModal" tabindex="-1" aria-labelledby="filtersModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-fullscreen-sm-down">
      <div class="modal-content bg-dark">
        <div class="modal-header border-secondary">
          <h5 class="modal-title text-white fs-4" id="filtersModalLabel">Filtros</h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="filters-section border-0 p-0">
          
            <!-- Type Filter -->
            <div class="category-filters mb-4">
              <div class="category-buttons d-flex flex-wrap gap-2">
                <button
                  class="category-btn filter-btn mb-2 w-100"
                  :class="{ 'active': !selectedTypes.length }"
                  @click="clearAllFilters"
                >
                  {{ isMerchPage ? 'MERCHANDISING' : 'CATÁLOGO' }}
                </button>
                <button
                  v-for="type in filteredTypeOptions"
                  :key="type.id"
                  class="category-btn filter-btn"
                  :class="{ 'active': selectedTypes.includes(type.id) }"
                  @click="toggleCategory(type.id)"
                >
                  {{ type.name.toUpperCase() }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import ProductCard from "@/components/ProductCard.vue";
import { getProductsPaginated, onProductsChanged } from "@/services/products";
import { apiErrorMessage } from '@/utils/apiErrors';
import { isAdminSession } from '@/utils/adminFreshness';
import './ProductList.css'; // Import the CSS file

const CATALOG_CATEGORIES_CACHE_KEY = 'catalogCategoriesReady';
const MIN_SKELETON_MS = 480;

const waitMinDuration = (startedAt, minMs = MIN_SKELETON_MS) => {
  const remaining = minMs - (Date.now() - startedAt);
  if (remaining <= 0) return Promise.resolve();
  return new Promise((resolve) => setTimeout(resolve, remaining));
};

export default {
  name: "ProductsList",
  components: {
    ProductCard,
  },
  props: {
    isMerchPage: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchQuery: "",
      selectedTypes: [],
      onlyDiscountedArray: [],
      selectedPriceFilter: [],
      selectedStockFilter: [],
      productsPerPage: 6,
      currentPage: 1,
      products: [],
      loadError: '',
      productRequestSeq: 0,
      totalProductsCount: 0,
      hasNextPage: false,
      isLoadingMore: false,
      isMobileLessThan400: window.innerWidth < 400,
      isLoading: sessionStorage.getItem(CATALOG_CATEGORIES_CACHE_KEY) !== 'true',
      isFiltering: false,
      skeletonActive: sessionStorage.getItem(CATALOG_CATEGORIES_CACHE_KEY) !== 'true',
      productsRevealed: false,
      onlyDiscounted: false,
      sortOrder: '',
      isUpdatingUrl: false,
      isApplyingUrlFilters: false,
      searchTimeout: null,
      typeOptions: [
        { id: "auto", name: "Autoflorecientes" },
        { id: "fem", name: "Fotoperiódicas" },
        { id: "limited", name: "Ediciones Limitadas" },
      ],
      priceOptions: [
        { id: "under-5000", name: "Menos de $5.000" },
        { id: "5000-10000", name: "$5.000 - $10.000" },
        { id: "10000-20000", name: "$10.000 - $20.000" },
        { id: "20000-50000", name: "$20.000 - $50.000" },
        { id: "over-50000", name: "Más de $50.000" },
      ],
      stockOptions: [
        { id: "with-stock", name: "Con stock disponible" },
      ],
      isMobile: window.innerWidth < 768,
    };
  },
  methods: {
    beginProductsLoading(showMainLoader = false, resetProducts = true) {
      this.productsRevealed = false;
      this.skeletonActive = true;

      if (showMainLoader) {
        this.isLoading = true;
        this.isFiltering = false;
      } else if (resetProducts) {
        this.isLoading = false;
        this.isFiltering = true;
      }

      return Date.now();
    },

    async finishProductsLoading(startedAt, seq) {
      await waitMinDuration(startedAt);
      if (seq !== this.productRequestSeq) return;
      this.isLoading = false;
      this.isFiltering = false;
      this.isLoadingMore = false;
      this.skeletonActive = false;
      await this.$nextTick();
      requestAnimationFrame(() => {
        if (seq === this.productRequestSeq) this.productsRevealed = true;
      });
    },

    async goToPage(page) {
      if (this.isLoadingMore || page === this.currentPage || page < 1 || page > this.totalPages) return;
      
      this.isLoadingMore = true;
      const seq = ++this.productRequestSeq;
      this.loadError = '';
      const startedAt = this.beginProductsLoading(false, true);
      try {
        this.currentPage = page;
        const filters = this.getActiveFilters();
        
        const response = await getProductsPaginated(this.currentPage, this.productsPerPage, filters);
        if (seq !== this.productRequestSeq) return;
        
        console.log('Page response received:', {
          results: response.results ? response.results.length : 0,
          count: response.count,
          hasNext: !!response.next,
          hasPrevious: !!response.previous
        });
        
        // Reemplazar los productos con los de la página actual
        this.products = response.results;
        this.totalProductsCount = response.count || 0; // Ensure count is never undefined
        this.hasNextPage = !!response.next;
        
        // Scroll to top of products container
        const productsContainer = document.querySelector('.products-grid');
        if (productsContainer) {
          productsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        
        console.log(`Cargada página ${this.currentPage}, productos totales: ${this.products.length} de ${this.totalProductsCount}`);
      } catch (error) {
        if (seq !== this.productRequestSeq) return;
        this.loadError = apiErrorMessage(error, 'No se pudo cargar esta página de productos.');
        console.error('Error loading page products:', error);
      } finally {
        await this.finishProductsLoading(startedAt, seq);
        if (seq === this.productRequestSeq) this.updateUrlParams();
      }
    },

    markCategoriesCached() {
      sessionStorage.setItem(CATALOG_CATEGORIES_CACHE_KEY, 'true');
    },

    prefetchAdjacentPages(filters) {
      if (isAdminSession()) return;
      const pagesToPrefetch = [];
      if (this.currentPage > 1) pagesToPrefetch.push(this.currentPage - 1);
      if (this.hasNextPage) pagesToPrefetch.push(this.currentPage + 1);

      pagesToPrefetch.forEach((page) => {
        getProductsPaginated(page, this.productsPerPage, filters).catch(() => {});
      });
    },
    
    async loadMore() {
      if (this.isLoadingMore || !this.hasNextPage) return;
      
      await this.goToPage(this.currentPage + 1);
    },
    
    clearAllFilters() {
      console.log('Clearing all filters');
      // Reset all filter states
      this.selectedTypes = []
      this.selectedPriceFilter = []
      this.selectedStockFilter = []
      this.onlyDiscounted = false
      this.onlyDiscountedArray = []
      this.searchQuery = ''
      this.sortOrder = ''
      
      // Update URL parameters to reflect cleared filters
      this.updateUrlParams()
      
      // Reload products with cleared filters
      this.$nextTick(() => {
        this.loadProducts(true, false) // Reset products, don't show main loader
      })
    },
      async applyUrlFilters() {
      if (this.isApplyingUrlFilters) return;
      this.isApplyingUrlFilters = true;
      
      try {
        this.parseUrlFilters();
        await this.loadProducts(true);
      } finally {
        this.isApplyingUrlFilters = false;
      }
    },

    parseUrlFilters() {
      const { category, categories, search } = this.$route.query;

      if (!category && !categories && !search) {
        return;
      }

      this.selectedTypes = [];
      this.onlyDiscounted = false;
      this.onlyDiscountedArray = [];
      this.selectedPriceFilter = [];
      this.selectedStockFilter = [];
      this.searchQuery = '';

      if (category === 'ofertas') {
        this.onlyDiscounted = true;
        this.onlyDiscountedArray = ['discount'];
      } else if (category) {
        const categoryMapping = {
          'automaticas': 'auto',
          'Fotoperiodicas': 'fem',
          'fotoperiodicas': 'fem',
          'Fotoperiódicas': 'fem',
          'regulares': 'reg'
        };

        const typeId = categoryMapping[category];
        if (typeId) {
          this.selectedTypes = [typeId];
        }
      } else if (categories) {
        const categoryMapping = {
          'automaticas': 'auto',
          'Fotoperiodicas': 'fem',
          'regulares': 'reg',
          'limitada': 'limited'
        };

        const categoryList = categories.split(',');
        const typeIds = categoryList
          .map(cat => categoryMapping[cat.trim()])
          .filter(Boolean);

        if (typeIds.length > 0) {
          this.selectedTypes = typeIds;
        }
      }

      if (search) {
        this.searchQuery = decodeURIComponent(search);
      }
    },
    
    updateUrlParams() {
      // Prevent multiple rapid updates
      if (this.isUpdatingUrl) return;
      this.isUpdatingUrl = true;

      try {
        const query = {};
        
        if (this.onlyDiscounted) {
          query.category = 'ofertas';
        } else if (this.selectedTypes.length > 0) {
          const typeMapping = {
            'auto': 'automaticas',
            'fem': 'Fotoperiodicas',
            'reg': 'regulares',
            'limited': 'limitada'
          };
          // Manejar múltiples tipos seleccionados - ensure consistency in spelling
          const categoryValues = this.selectedTypes.map(type => typeMapping[type]).filter(Boolean);
          console.log('Category values for URL:', categoryValues);
          if (categoryValues.length > 0) {
            query.categories = categoryValues.join(',');
          }
        }
        
        if (this.searchQuery) {
          query.search = encodeURIComponent(this.searchQuery);
        }
        
        // Para mantener retrocompatibilidad con el formato anterior
        // Si solo hay una categoría y no está usando el nuevo formato, usar el viejo
        if (query.categories && query.categories.split(',').length === 1) {
          query.category = query.categories;
          delete query.categories;
        }

        // Verify if URL actually needs updating to prevent recursion
        const currentQueryStr = JSON.stringify(this.$route.query);
        const newQueryStr = JSON.stringify(query);
        
        if (currentQueryStr !== newQueryStr) {
          this.$router.replace({ query }).catch(() => {});
        }
      } finally {
        // Asegurarse de que isUpdatingUrl se restablece después de un tiempo
        setTimeout(() => {
          this.isUpdatingUrl = false;
        }, 300);
      }
    },
    
    getActiveFilters() {
      const filters = {};
      
      // If we're on merch page, always filter by merch type
      if (this.isMerchPage) {
        filters.type = ['merch'];
      } else {
        // On regular products page, we need to exclude merch products
        if (this.selectedTypes.length > 0) {
          // Check for limited edition filter
          const hasLimitedFilter = this.selectedTypes.includes('limited');
          
          // Filter regular types (auto, fem, etc)
          const regularTypes = this.selectedTypes.filter(type => type !== 'limited');
          
          if (regularTypes.length > 0) {
            // Explicitly set the type filter to exactly match the selected types
            filters.type = regularTypes;
            console.log('Setting type filter to:', regularTypes);
          } else if (!hasLimitedFilter) {
            // If no regular types and no limited filter, don't specify type
            // This allows all products to be shown
          }
          
          // Add limited_edition filter if limited edition is selected
          if (hasLimitedFilter) {
            filters.limitedEdition = true;
            console.log('Setting limitedEdition filter for limited edition products');
          }
        } else {
          // If no types selected, don't apply type filter to show all products
          console.log('No types selected, showing all products');
        }
        // No matter what, we add a parameter to exclude merch products
        filters.excludeTypes = ['merch'];
      }
      
      if (this.searchQuery) {
        filters.search = this.searchQuery;
      }
      
      if (this.onlyDiscounted) {
        filters.onlyDiscounted = true;
      }
      
      if (this.selectedPriceFilter.length > 0) {
        filters.priceRange = this.selectedPriceFilter;
      }
      
      if (this.selectedStockFilter.length > 0) {
        // Separar el filtro "Sin stock" de otros filtros de stock
        const outOfStockFilter = this.selectedStockFilter.includes('out-of-stock');
        const otherStockFilters = this.selectedStockFilter.filter(filter => filter !== 'out-of-stock');
        
        if (outOfStockFilter) {
          filters.noStock = true;
        }
        if (otherStockFilters.length > 0) {
          filters.stockFilter = otherStockFilters;
        }
      }
      
      if (this.sortOrder) {
        const orderingMap = {
          'low': 'min_price',
          'high': '-min_price'
        };
        filters.ordering = orderingMap[this.sortOrder];
      }
      
      return filters;
    },

    async loadProducts(resetProducts = true, showMainLoader = false) {
      const seq = ++this.productRequestSeq;
      this.loadError = '';
      const startedAt = this.beginProductsLoading(showMainLoader, resetProducts);
      
      try {
        if (resetProducts) {
          this.products = [];
          this.currentPage = 1;
        }
        
        const filters = this.getActiveFilters();
        console.log('LoadProducts called with filters:', filters);
        console.log('Selected brands:', this.selectedBrands);
        
        const response = await getProductsPaginated(this.currentPage, this.productsPerPage, filters);
        if (seq !== this.productRequestSeq) return;
        console.log('Products response received:', response);
        
        if (resetProducts) {
          this.products = response.results;
        } else {
          this.products = [...this.products, ...response.results];
        }
        
        this.totalProductsCount = response.count;
        this.hasNextPage = !!response.next;
        
        console.log('Products loaded:', this.products.length, 'of', this.totalProductsCount);
        this.markCategoriesCached();
        this.prefetchAdjacentPages(filters);
      } catch (error) {
        if (seq !== this.productRequestSeq) return;
        this.loadError = apiErrorMessage(error, 'No se pudo cargar el catálogo.');
        console.error('Error loading products:', error);
    } finally {
      await this.finishProductsLoading(startedAt, seq);
    }
  },

  checkIfMobile() {
    this.isMobile = window.innerWidth < 768;
  },

  toggleCategory(categoryId) {
    const index = this.selectedTypes.indexOf(categoryId);
    if (index === -1) {
      // Add the category to the filter
      // Special handling for limited editions
      if (categoryId === 'limited') {
        // For limited editions, we want to filter by the limited_edition field
        this.selectedTypes = [categoryId]; 
        console.log('Limited edition selected, will filter by limited_edition=true');
      } else {
        // For other categories, replace with just this category
        this.selectedTypes = [categoryId];
      }
    } else {
      // Remove the category (deselect)
      this.selectedTypes.splice(index, 1);
    }
    
    console.log(`Category ${categoryId} toggled, selectedTypes:`, this.selectedTypes);
  },
},
  async created() {
    const categoriesCached = sessionStorage.getItem(CATALOG_CATEGORIES_CACHE_KEY) === 'true';
    const hasUrlFilters = Object.keys(this.$route.query).length > 0;

    try {
      if (categoriesCached) {
        this.isLoading = false;
        this.isFiltering = true;
      }

      if (hasUrlFilters) {
        this.isUpdatingUrl = true;
        this.isApplyingUrlFilters = true;
        this.parseUrlFilters();
        this.isApplyingUrlFilters = false;
      }

      await this.loadProducts(true, !categoriesCached);

      if (hasUrlFilters) {
        this.isUpdatingUrl = false;
      }

      document.title = "Catálogo de semillas - Kame Seeds";
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      this.isLoading = false;
      this.isUpdatingUrl = false;
    }
  },
  async mounted() {
    this.stopProductChanges = onProductsChanged(() => {
      if (isAdminSession()) this.loadProducts(true);
    });
    this.refreshVisibleProducts = () => {
      if (document.visibilityState === 'visible' && isAdminSession()) this.loadProducts(true);
    };
    document.addEventListener('visibilitychange', this.refreshVisibleProducts);
    await nextTick();
    // Detect mobile screen size
    this.checkIfMobile();
    window.addEventListener('resize', this.checkIfMobile);
  },
  beforeUnmount() {
    this.productRequestSeq += 1;
    this.stopProductChanges?.();
    document.removeEventListener('visibilitychange', this.refreshVisibleProducts);
    window.removeEventListener('resize', this.checkIfMobile);
    // Limpiar timeout de búsqueda
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
  watch: {
    onlyDiscountedArray: {
      handler(newVal) {
        console.log('💰 onlyDiscountedArray watcher triggered:', newVal);
        this.onlyDiscounted = newVal.includes('discount');
      },
      deep: true,
      immediate: false
    },
    selectedTypes: {
      handler(newVal, oldVal) {
        console.log('🏷️ selectedTypes watcher triggered:', { 
          newVal, 
          oldVal,
          isUpdatingUrl: this.isUpdatingUrl,
          isApplyingUrlFilters: this.isApplyingUrlFilters 
        });
        
        if (!this.isUpdatingUrl && !this.isApplyingUrlFilters) {
          console.log('selectedTypes changed, updating products...');
          this.updateUrlParams();
          
          // Force a reload of products with the new filter
          this.$nextTick(() => {
            this.loadProducts(true, false); // Reset products, don't show main loader
          });
        }
      },
      deep: true,
      immediate: false
    },
    searchQuery: {
      handler(newVal, oldVal) {
        console.log('🔍 searchQuery watcher triggered:', { 
          newVal, 
          oldVal,
          isUpdatingUrl: this.isUpdatingUrl,
          isApplyingUrlFilters: this.isApplyingUrlFilters 
        });
        
        if (!this.isUpdatingUrl && !this.isApplyingUrlFilters) {
          this.updateUrlParams();
          // Debounce la búsqueda para evitar demasiadas llamadas
          clearTimeout(this.searchTimeout);
          this.searchTimeout = setTimeout(() => {
            this.loadProducts(true); // Sin mostrar skeleton
          }, 500);
        }
      },
      immediate: false
    },
    onlyDiscounted: {
      handler(newVal, oldVal) {
        console.log('💰 onlyDiscounted watcher triggered:', { 
          newVal, 
          oldVal,
          isUpdatingUrl: this.isUpdatingUrl,
          isApplyingUrlFilters: this.isApplyingUrlFilters 
        });
        
        if (!this.isUpdatingUrl && !this.isApplyingUrlFilters) {
          console.log('onlyDiscounted changed, updating products...');
          this.updateUrlParams();
          this.loadProducts(true); // Sin mostrar skeleton
        }
      },
      immediate: false
    },
    sortOrder: {
      handler(newVal, oldVal) {
        console.log('📊 sortOrder watcher triggered:', { 
          newVal, 
          oldVal,
          isUpdatingUrl: this.isUpdatingUrl,
          isApplyingUrlFilters: this.isApplyingUrlFilters 
        });
        
        if (!this.isUpdatingUrl && !this.isApplyingUrlFilters) {
          console.log('sortOrder changed, updating products...');
          this.loadProducts(true); // Sin mostrar skeleton
        }
      },
      immediate: false
    },
    selectedPriceFilter: {
      handler(newVal, oldVal) {
        console.log('💲 selectedPriceFilter watcher triggered:', { 
          newVal, 
          oldVal,
          isUpdatingUrl: this.isUpdatingUrl,
          isApplyingUrlFilters: this.isApplyingUrlFilters 
        });
        
        if (!this.isUpdatingUrl && !this.isApplyingUrlFilters) {
          console.log('priceFilter changed, updating products...');
          this.updateUrlParams();
          this.loadProducts(true); // Sin mostrar skeleton
        }
      },
      deep: true,
      immediate: false
    },
    selectedStockFilter: {
      handler(newVal, oldVal) {
        console.log('📦 selectedStockFilter watcher triggered:', { 
          newVal, 
          oldVal,
          isUpdatingUrl: this.isUpdatingUrl,
          isApplyingUrlFilters: this.isApplyingUrlFilters 
        });
        
        if (!this.isUpdatingUrl && !this.isApplyingUrlFilters) {
          console.log('stockFilter changed, updating products...');
          this.updateUrlParams();
          this.loadProducts(true); // Sin mostrar skeleton
        }
      },
      deep: true,
      immediate: false
    }
  },
  computed: {
    showProductsSkeleton() {
      return this.skeletonActive;
    },
    // Filter type options based on whether we're on merch page or regular catalog page
    filteredTypeOptions() {
      if (this.isMerchPage) {
        // On merch page, we only want to show merch filter options (if any)
        return this.typeOptions.filter(type => type.id === 'merch');
      } else {
        // On regular page, exclude merch option
        return this.typeOptions.filter(type => type.id !== 'merch');
      }
    },
    filteredProducts() {
      // Con la nueva paginación, los productos ya vienen filtrados del servidor
      // Solo necesitamos aplicar el filtrado local si es necesario
      return this.products;
    },
    totalProducts() {
      return this.totalProductsCount;
    },
    hasMoreProducts() {
      return this.hasNextPage;
    },
    totalPages() {
      // Make sure totalProductsCount is treated as a number and has a fallback
      const count = parseInt(this.totalProductsCount) || this.products.length || 0;
      return Math.max(Math.ceil(count / this.productsPerPage) || 1, 1);
    },
    paginationItems() {
      if (this.totalPages <= 7) {
        // Si hay 7 páginas o menos, mostrar todas
        return Array.from({ length: this.totalPages }, (_, i) => i + 1);
      }
      
      // Caso especial: más de 7 páginas, usar elipsis
      const items = [];
      
      // Siempre mostrar la primera página
      items.push(1);
      
      if (this.currentPage > 3) {
        // Agregar elipsis si la página actual está lejos del inicio
        items.push('...');
      }
      
      // Páginas alrededor de la página actual
      const start = Math.max(2, this.currentPage - 1);
      const end = Math.min(this.totalPages - 1, this.currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        items.push(i);
      }
      
      if (this.currentPage < this.totalPages - 2) {
        // Agregar elipsis si la página actual está lejos del final
        items.push('...');
      }
      
      // Siempre mostrar la última página
      items.push(this.totalPages);
      
      return items;
    },
    hasActiveFilters() {
      return this.selectedTypes.length > 0 || 
             this.onlyDiscounted ||
             this.searchQuery.length > 0
    },
    discountFilter: {
      get() {
        return this.onlyDiscounted ? ['discount'] : []
      },
      set(value) {
        this.onlyDiscounted = value.includes('discount')
      }
    }
  }
};
</script>

<style scoped>
.product-list-section {
  min-height: 100vh;
  padding-bottom: 4rem;
}
.title, .category-btn {
  font-size: 50px;
}


.filters-section {
  padding: 0;
}
/* Catalog Title */
.catalog-title {
  background-color: var(--primary-color);
  padding: 8px 16px;
  display: inline-block;
}

.catalog-title h1 {
  font-size: 1.5rem;
  padding: 0.5rem 1rem;
  margin: 0;
}

/* Category Filter Buttons */
.category-filters .category-buttons {
  gap: 0.5rem;
  background: transparent !important;
}

.category-btn {
  color: var(--black-color);
  padding: 0.5rem 1rem;
  transition: all 0.3s ease;
  cursor: pointer;
  font-weight: 500;
  width: 100%;
  text-align: center;
  text-transform: uppercase;
  background-color: transparent;
}

.filter-btn {
  font-size: 1.5rem;
  font-weight: bold;
  padding: 8px 16px;
  letter-spacing: .06rem;
}
.category-btn{
  background: rgba(255, 255, 255, 0.8);
  min-width: 80px;
}

.category-btn:hover {
  background: var(--primary-color);
  border-color: var(--primary-color);
  filter: brightness(1.1);
  color: var(--black-color);
}

.category-btn.active {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: var(--black-color);
  position: relative;
}

.category-btn.active:hover {
  background: var(--primary-color);
  border-color: var(--primary-color);
  filter: brightness(1.1);
}

.filter-btn.active:after {
  content: '';
  position: absolute;
  bottom: -3px;
  left: 0;
  width: 100%;
  height: 2px;
}

/* Responsive adjustments */
@media (max-width: 576px) {
  .category-btn {
    font-size: 0.8rem;
    padding: 0.4rem 0.8rem;
    min-width: 70px;
  }
  
  .filter-btn {
    font-size: 1.2rem;
    padding: 8px 12px;
    margin-bottom: 6px;
  }
  
  .catalog-title {
    padding: 6px 12px;
  }
  
  .catalog-title h1 {
    font-size: 1.2rem;
    padding: 0.4rem 0.8rem;
  }
}

/* Mejorar la visibilidad del botón del modal en dispositivos móviles */
.modal-footer .btn-success {
  background-color: #28a745;
  font-size: 1.1rem;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.modal-footer .btn-success:hover,
.modal-footer .btn-success:active {
  background-color: #218838;
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
}

@media (max-width: 767px) {
  .modal-content {
    border: none;
    border-radius: 16px;
  }
  
  .modal-footer {
    padding: 1rem;
    margin-bottom: 2.5rem;
  }
  
  .modal-header {
    padding: 1.2rem 1rem;
  }
}

/* Pagination Styling */
.pagination-container {
  margin-top: 2.5rem;
  margin-bottom: 1.5rem;
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.pagination-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  padding: 0 12px;
  margin: 0 2px;
  background-color: rgba(255, 255, 255, .8);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #000;
  font-weight: 500;
  border-radius: 0.25rem;
  transition: all 0.3s ease;
  cursor: pointer;
}

.pagination-btn:hover:not(.active):not(.disabled) {
  background-color: rgba(233, 233, 233, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
}

.pagination-btn.active {
  background-color: var(--primary-color);
  border-color: var(--black-color);
  color: var(--black-color);
  font-weight: 700;
}

.pagination-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.pagination-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  padding: 0 8px;
  color: #fff;
}

.products-grid-content {
  opacity: 0;
  transition: opacity 0.55s ease;
  will-change: opacity;
}

.products-grid-content.is-visible {
  opacity: 1;
}

.skeleton-layer {
  animation: skeleton-fade-in 0.25s ease;
}

@keyframes skeleton-fade-in {
  from { opacity: 0.6; }
  to { opacity: 1; }
}

@media (max-width: 576px) {
  .pagination-btn {
    min-width: 36px;
    height: 36px;
    font-size: 0.9rem;
  }
  
  .pagination-ellipsis {
    min-width: 30px;
    height: 36px;
  }
}
</style>
